import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FiArrowLeft, FiEdit, FiMail, FiPhone, FiActivity, FiCalendar, FiCheckCircle, FiClock, FiCircle, FiFolder, FiList, FiSave, FiX, FiPlus, FiTrash2, FiKey, FiSearch, FiLock } from 'react-icons/fi';
// Only using real data from Supabase
import ProjectDashboard from './ProjectDashboard';
import ClientFileManager from './ClientFileManager';
import TasksPage from './TasksPage';
import ProjectEditor from './ProjectEditor';
import ClientChecklist from './ClientChecklist';
import Client004Checklist from './Client004Checklist';
import Client004WebsiteQuestionnaire from './Client004WebsiteQuestionnaire';
import SEOAuditPage from './SEOAuditPage';
import FundraiserPage from './FundraiserPage';
import ClientDashboardAI from './ClientDashboardAI';
import KeywordsPage from './KeywordsPage';
import CompetitorAnalysisPage from './CompetitorAnalysisPage';
import { renderIcon } from '../utils/iconUtils';
import { getClientById, updateClient, Client } from '../utils/clientService';
import { supabase } from '../utils/supabaseClient';
import { getProjectsByClientId, createProject, deleteProject, updateProject, createTask, updateTask, deleteTask, Project, Task } from '../utils/projectService';

// --- Styled Components ---
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: #667085;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: #d92d20;
  background: #fff1f2;
  padding: 20px;
  border-radius: 8px;
`;

const ClientDashboardContainer = styled.div`
  padding: 40px;
  background: #f4f7f9;
  min-height: 100vh;
  max-width: 1400px;
  margin: 0 auto;
  color: #101828; /* base dark text across all tabs */

  /* Ensure typical content tags inherit readable color */
  p, span, li, td, th, label, input, textarea, select {
    color: inherit;
  }
`;

const ClientHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
`;

const BackToListButton = styled.button`
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 10px 16px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 20px;
  transition: all 0.2s ease;
  &:hover {
    background: #f9fafb;
  }
`;

const ClientHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClientLogo = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  color: #475569;
  margin-right: 20px;
`;

const ClientName = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #101828;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 30px;
  border-bottom: 1px solid #e2e8f0;
`;

interface TabButtonProps {
  $active: boolean;
  $locked?: boolean;
}

const TabButton = styled.button<TabButtonProps>`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#4c6fff' : 'transparent'};
  padding: 12px 16px;
  color: ${props => props.$locked ? '#98a2b3' : (props.$active ? '#4c6fff' : '#667085')};
  font-size: 15px;
  font-weight: 500;
  cursor: ${props => props.$locked ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.$locked ? 0.6 : 1};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: -1px;
  
  &:hover {
    background: ${props => props.$locked ? 'transparent' : '#f9fafb'};
    color: ${props => props.$locked ? '#98a2b3' : '#4c6fff'};
  }
`;

const OverviewContent = styled.div``;

const ClientInfoCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  border: 1px solid #e2e8f0;
`;

const ClientInfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  h3 {
    margin: 0;
    font-size: 20px;
    color: #101828;
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ClientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #667085;
  margin-bottom: 6px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #344054;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #101828;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4c6fff;
    box-shadow: 0 0 0 3px rgba(76, 111, 255, 0.15);
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fff1f2;
  border: 1px solid #ffdde0;
  color: #d92d20;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background: #f0fdf4;
  border: 1px solid #a7f3d0;
  color: #15803d;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const MetricsSection = styled.div`
  margin-bottom: 30px;
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 20px;
    color: #101828;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
`;

const MetricCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
`;

const MetricIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #eef2ff;
  color: #4c6fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-right: 16px;
`;

const MetricInfo = styled.div``;

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: #101828;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #667085;
`;

const ProjectCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ProjectTitle = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
`;

interface StatusBadgeProps {
  $status: string;
}

const StatusBadge = styled.span<StatusBadgeProps>`
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 16px;
  font-weight: 500;
  text-transform: capitalize;

  background: ${props => 
    props.$status === 'completed' ? '#ecfdf5' : 
    props.$status === 'in-progress' ? '#eff6ff' : 
    '#fffbeb'};
  color: ${props => 
    props.$status === 'completed' ? '#067647' : 
    props.$status === 'in-progress' ? '#1d4ed8' : 
    '#b45309'};
`;

interface ProjectProgressProps {
  $progress: number;
}

const ProjectProgress = styled.div<ProjectProgressProps>`
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 12px 0 16px;
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => `${props.$progress}%`};
    background: #4c6fff;
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const ProjectDates = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #667085;
`;

const TaskSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
`;

const TaskStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #667085;
`;

const ProjectsContent = styled.div`
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 20px;
    color: #101828;
  }
`;

const ProjectsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CreateProjectButton = styled.button`
  padding: 10px 16px;
  background: #4c6fff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #3a5ce0;
  }
  
  svg {
    font-size: 16px;
  }
`;

const ProjectDetail = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  border: 1px solid #e2e8f0;
`;

const ProjectDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h3 {
    margin: 0;
    font-size: 24px;
    color: #101828;
  }
`;

const ProjectActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  padding: 10px 16px;
  background: #fff;
  color: #344054;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
  }
  
  svg {
    font-size: 16px;
  }
`;

const ProjectDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const DetailItem = styled.div`
  background: #f9fafb;
  padding: 16px;
  border-radius: 8px;
`;

const DetailLabel = styled.div`
  font-size: 13px;
  color: #667085;
  margin-bottom: 6px;
`;

const DetailValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #101828;
`;

const TasksTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  
  th, td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  th {
    font-weight: 500;
    color: #667085;
    font-size: 13px;
    text-transform: uppercase;
  }
  
  td {
    font-size: 14px;
    color: #344054;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ProjectCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
`;

const CardActionButton = styled.button`
  padding: 8px 14px;
  background: #fff;
  color: #344054;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: #f9fafb;
  }
  
  svg {
    font-size: 14px;
  }
`;

const AllProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
`;

const AnalyticsContent = styled.div`
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 20px;
    color: #101828;
  }

  p {
    color: #667085;
    margin-bottom: 20px;
  }
`;

const ComingSoonMessage = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px;
  color: #101828;

  h3 {
    font-size: 20px;
    margin-bottom: 12px;
  }

  p {
    color: #667085;
  }

  ul {
    margin-top: 16px;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
      color: #667085;
    }
  }
`;
// --- End Styled Components ---

interface ClientDashboardProps {
  clientId: string;
  onBack: () => void;
}

// Using Project and Task interfaces from projectService

const ClientDashboard: React.FC<ClientDashboardProps> = ({ clientId, onBack }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'files' | 'tasks' | 'checklist' | 'website-questionnaire' | 'analytics' | 'fundraiser' | 'email' | 'seo' | 'keywords' | 'competitor-analysis' | 'landing-pages' | 'ai-tools'>('overview');
  const [client, setClient] = useState<Client | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedClient, setEditedClient] = useState<Client | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreatingProject, setIsCreatingProject] = useState<boolean>(false);
  const isEditingRef = useRef<boolean>(false);
  const navigate = useNavigate();

  // Handle navigation to Email Marketing Hub when the email tab is selected
  useEffect(() => {
    if (activeTab === 'email') {
      navigate(`/email-marketing?clientId=${clientId}`);
    }
  }, [activeTab, clientId, navigate]);

  // Function to fetch projects
  const fetchProjects = async () => {
    try {
      const projectsData = await getProjectsByClientId(clientId);
      console.log("Fetched projects:", projectsData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Handle saving client changes
  const handleSaveClientChanges = async () => {
    if (!editedClient) return;
    
    try {
      setSaveError(null);
      setSaveSuccess(false);
      
      // Call the update function from clientService
      await updateClient(editedClient.id, editedClient);
      
      // Update the local state
      setClient(editedClient);
      setIsEditing(false);
      isEditingRef.current = false;
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving client changes:', error);
      setSaveError('Failed to save changes. Please try again.');
    }
  };

  // Handle canceling edit mode
  const handleCancelEditClient = () => {
    setIsEditing(false);
    isEditingRef.current = false;
    setSaveError(null);
    // Reset edited client to original client data
    setEditedClient(client);
  };

  // Handle deleting a project
  const handleDeleteProject = async (projectId: string | null) => {
    if (!projectId) return;
    
    try {
      await deleteProject(projectId);
      
      // Update the projects list
      setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
      
      // If we're viewing the deleted project, go back to the projects list
      if (selectedProject && selectedProject.id === projectId) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      // Show error message to user
    }
  };

  // Function to handle creating a new project
  const handleCreateProject = () => {
    console.log("Creating new project for client:", clientId);
    setSelectedProject(null);
    setIsCreatingProject(true);
  };

  // Fetch client and projects data
  useEffect(() => {
    const fetchClientAndProjects = async () => {
      // Defensive: Don't fetch if clientId is missing
      if (!clientId) {
        setError('Client ID is missing.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        // --- CHANGE: Use getClientById instead of getClientByUserId ---
        const clientData = await getClientById(clientId);

        if (!clientData) {
          throw new Error('Client not found');
        }

        setClient(clientData);
        setEditedClient(JSON.parse(JSON.stringify(clientData))); // Deep copy for editing

        // Fetch projects for this client
        fetchProjects();
      } catch (error) {
        console.error('Error fetching client data:', error);
        setError('Failed to load client data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientAndProjects();
  }, [clientId]);

  // Handle clicks outside of edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only process if we're in edit mode
      if (!isEditingRef.current) return;
      
      // Check if the click was outside the client info card
      const clientInfoCard = document.querySelector('.client-info-card');
      if (clientInfoCard && !clientInfoCard.contains(event.target as Node)) {
        // Save changes automatically when clicking outside
        handleSaveClientChanges();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editedClient]);

  // Import AnalyticsDashboard at the top of the file
  const AnalyticsDashboard = React.lazy(() => import('./AnalyticsDashboard'));

  // Render the content based on active tab
  const renderContent = () => {
    // For clients without marketing plan, if they try to access a marketing tab, show marketing message
    if ((client?.id === SOUTH_TEXAS_FAD_CLIENT_ID || client?.id === 'client-grayson-burnette') && 
        ['analytics', 'email', 'seo', 'keywords', 'competitor-analysis', 'landing-pages', 'ai-tools'].includes(activeTab)) {
      return (
        <ComingSoonMessage>
          <h3>Upgrade to Marketing Services</h3>
          <p>This feature is available exclusively for our marketing clients. Upgrade your plan to access powerful marketing tools and analytics.</p>
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => window.open('mailto:sales@marketnest.com?subject=Interested in Marketing Services&body=I am interested in learning more about your marketing services.', '_blank')}
              style={{
                background: 'rgba(31, 83, 255, 0.25)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                padding: '10px 15px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FiMail size={16} /> Contact Sales
            </button>
          </div>
        </ComingSoonMessage>
      );
    }
    
    if (loading) {
      return <LoadingContainer>Loading client data...</LoadingContainer>;
    }
    
    if (error) {
      return <ErrorContainer>{error}</ErrorContainer>;
    }
    
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewContent>
            <ClientInfoCard className="client-info-card">
              <ClientInfoHeader>
                <h3>Client Information</h3>
                {isEditing ? (
                  <EditActions>
                    <button 
                      type="button" 
                      style={{ 
                        zIndex: 1000, 
                        background: 'rgba(31, 83, 255, 0.3)',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: 'white',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontWeight: 'bold'
                      }} 
                      onClick={handleSaveClientChanges}>
                      {renderIcon(FiSave)} Save
                    </button>
                    <button type="button" 
                      style={{ 
                        zIndex: 1000, 
                        background: 'rgba(255, 59, 48, 0.3)',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: 'white',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontWeight: 'bold'
                      }} 
                      onClick={handleCancelEditClient}>
                      {renderIcon(FiX)} Cancel
                    </button>
                  </EditActions>
                ) : (
                  <button 
                    type="button"
                    style={{ 
                      cursor: 'pointer', 
                      position: 'relative', 
                      zIndex: 1000,
                      background: 'rgba(31, 83, 255, 0.3)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'white',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontWeight: 'bold'
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('[ClientDashboard] Starting to edit client info - DIRECT METHOD');
                      if (client) {
                        // Ensure we have a fresh copy of the client data
                        setEditedClient(JSON.parse(JSON.stringify(client)));
                      }
                      // Update both the state and the ref
                      isEditingRef.current = true;
                      setIsEditing(true);
                      console.log('[ClientDashboard] Set isEditing to true - DIRECT METHOD');
                      // Force focus on the first input after a short delay
                      setTimeout(() => {
                        const firstInput = document.querySelector('input') as HTMLInputElement;
                        if (firstInput) {
                          firstInput.focus();
                        }
                      }, 100);
                    }}
                  >
                    {renderIcon(FiEdit)} Edit
                  </button>
                )}
              </ClientInfoHeader>

              {saveError && <ErrorMessage>{saveError}</ErrorMessage>}
              {saveSuccess && <SuccessMessage>Changes saved successfully!</SuccessMessage>}

              <ClientInfoGrid>
                {/* Name field - first in the list */}
                <InfoItem>
                  <InfoLabel>Company Name</InfoLabel>
                  {isEditing ? (
                    <EditInput 
                      type="text" 
                      value={editedClient?.name || ''} 
                      onChange={(e) => {
                        if (editedClient) {
                          setEditedClient({...editedClient, name: e.target.value});
                        }
                      }}
                    />
                  ) : (
                    <InfoValue>{client?.name || 'N/A'}</InfoValue>
                  )}
                </InfoItem>

                {/* Industry field */}
                <InfoItem>
                  <InfoLabel>Industry</InfoLabel>
                  {isEditing ? (
                    <EditInput 
                      type="text" 
                      value={editedClient?.industry || ''} 
                      onChange={(e) => {
                        if (editedClient) {
                          setEditedClient({...editedClient, industry: e.target.value});
                        }
                      }}
                    />
                  ) : (
                    <InfoValue>{client?.industry || 'N/A'}</InfoValue>
                  )}
                </InfoItem>

                {/* Contact Name field */}
                <InfoItem>
                  <InfoLabel>Contact Name</InfoLabel>
                  {isEditing ? (
                    <EditInput 
                      type="text" 
                      value={editedClient?.contactname || ''} 
                      onChange={(e) => {
                        if (editedClient) {
                          setEditedClient({...editedClient, contactname: e.target.value});
                        }
                      }}
                    />
                  ) : (
                    <InfoValue>{client?.contactname || 'N/A'}</InfoValue>
                  )}
                </InfoItem>

                {/* Contact Email field */}
                <InfoItem>
                  <InfoLabel>Contact Email</InfoLabel>
                  {isEditing ? (
                    <EditInput 
                      type="email" 
                      value={editedClient?.contactemail || ''} 
                      onChange={(e) => {
                        if (editedClient) {
                          setEditedClient({...editedClient, contactemail: e.target.value});
                        }
                      }}
                    />
                  ) : (
                    <InfoValue>
                      {renderIcon(FiMail)}
                      <span style={{ marginLeft: '5px' }}>{client?.contactemail || 'N/A'}</span>
                    </InfoValue>
                  )}
                </InfoItem>

                {/* Contact Phone field */}
                <InfoItem>
                  <InfoLabel>Contact Phone</InfoLabel>
                  {isEditing ? (
                    <EditInput 
                      type="tel" 
                      value={editedClient?.contactphone || ''} 
                      onChange={(e) => {
                        if (editedClient) {
                          setEditedClient({...editedClient, contactphone: e.target.value});
                        }
                      }}
                    />
                  ) : (
                    <InfoValue>
                      {renderIcon(FiPhone)}
                      <span style={{ marginLeft: '5px' }}>{client?.contactphone || 'N/A'}</span>
                    </InfoValue>
                  )}
                </InfoItem>

                {/* Status field */}
                <InfoItem>
                  <InfoLabel>Status</InfoLabel>
                  {isEditing ? (
                    <EditInput 
                      type="text" 
                      value={editedClient?.status || ''} 
                      onChange={(e) => {
                        if (editedClient) {
                          setEditedClient({...editedClient, status: e.target.value});
                        }
                      }}
                    />
                  ) : (
                    <InfoValue>
                      <StatusBadge $status={client?.status || 'active'}>
                        {client?.status || 'Active'}
                      </StatusBadge>
                    </InfoValue>
                  )}
                </InfoItem>
              </ClientInfoGrid>
            </ClientInfoCard>

            <MetricsSection>
              <h3>Client Metrics</h3>
              <MetricsGrid>
                <MetricCard>
                  <MetricIcon>{renderIcon(FiActivity)}</MetricIcon>
                  <MetricInfo>
                    <MetricValue>{client?.activeprojects || 0}</MetricValue>
                    <MetricLabel>Active Projects</MetricLabel>
                  </MetricInfo>
                </MetricCard>
                
                <MetricCard>
                  <MetricIcon>{renderIcon(FiCalendar)}</MetricIcon>
                  <MetricInfo>
                    <MetricValue>
                      {projects.filter(p => p.status === 'completed').length}
                    </MetricValue>
                    <MetricLabel>Completed Projects</MetricLabel>
                  </MetricInfo>
                </MetricCard>
                
                <MetricCard>
                  <MetricIcon>{renderIcon(FiCheckCircle)}</MetricIcon>
                  <MetricInfo>
                    <MetricValue>
                      {Math.round(projects.reduce((acc, p) => acc + p.progress, 0) / (projects.length || 1))}%
                    </MetricValue>
                    <MetricLabel>Average Progress</MetricLabel>
                  </MetricInfo>
                </MetricCard>
              </MetricsGrid>
            </MetricsSection>

            <h3>Recent Projects</h3>
            <AllProjectsGrid>
              {projects.slice(0, 3).map(project => (
                <ProjectCard key={project.id} onClick={() => setSelectedProject(project)}>
                  <ProjectHeader>
                    <ProjectTitle>{project.name}</ProjectTitle>
                    <StatusBadge $status={project.status}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </StatusBadge>
                  </ProjectHeader>
                  
                  <ProjectProgress $progress={project.progress} />
                  
                  <ProjectDates>
                    <span>Start: {new Date(project.start_date).toLocaleDateString()}</span>
                    <span>Due: {new Date(project.due_date).toLocaleDateString()}</span>
                  </ProjectDates>
                  
                  <ProjectCardActions>
                    <CardActionButton onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProject(project);
                      setActiveTab('projects');
                    }}>
                      {renderIcon(FiFolder)} View Details
                    </CardActionButton>
                    
                    <CardActionButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project.id);
                      }}
                      style={{ background: 'rgba(255, 59, 48, 0.2)' }}
                    >
                      {renderIcon(FiTrash2)} Delete
                    </CardActionButton>
                  </ProjectCardActions>
                </ProjectCard>
              ))}
            </AllProjectsGrid>
          </OverviewContent>
        );
        
      case 'projects':
        return (
          <ProjectsContent>
            {selectedProject ? (
              <div>
                <ProjectDetailHeader>
                  <h3>{selectedProject.name}</h3>
                  <ProjectActionButtons>
                    <ActionButton onClick={() => setSelectedProject(null)}>
                      {renderIcon(FiArrowLeft)} Back to Projects
                    </ActionButton>
                  </ProjectActionButtons>
                </ProjectDetailHeader>
                
                <ProjectDetail>
                  <ProjectDetailGrid>
                    <DetailItem>
                      <DetailLabel>Status</DetailLabel>
                      <DetailValue>
                        <StatusBadge $status={selectedProject.status}>
                          {selectedProject.status.charAt(0).toUpperCase() + selectedProject.status.slice(1)}
                        </StatusBadge>
                      </DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Progress</DetailLabel>
                      <DetailValue>{selectedProject.progress}%</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Start Date</DetailLabel>
                      <DetailValue>
                        {new Date(selectedProject.start_date).toLocaleDateString()}
                      </DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Due Date</DetailLabel>
                      <DetailValue>
                        {new Date(selectedProject.due_date).toLocaleDateString()}
                      </DetailValue>
                    </DetailItem>
                  </ProjectDetailGrid>
                  
                  <h4>Description</h4>
                  <p>{selectedProject.description}</p>
                  
                  <h4>Tasks</h4>
                  <TasksTable>
                    <thead>
                      <tr>
                        <th>Task Name</th>
                        <th>Status</th>
                        <th>Assignee</th>
                        <th>Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* This would be populated with actual tasks */}
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center' }}>
                          No tasks found for this project.
                        </td>
                      </tr>
                    </tbody>
                  </TasksTable>
                </ProjectDetail>
              </div>
            ) : isCreatingProject ? (
              <ProjectEditor 
                clientId={clientId}
                project={selectedProject}
                onSave={(newProject) => {
                  console.log("Project saved:", newProject);
                  // Simply add the new project to the list
                  setProjects(prev => {
                    if (selectedProject) {
                      return prev.map(p => p.id === newProject.id ? newProject : p);
                    }
                    return [...prev, newProject];
                  });
                  setIsCreatingProject(false);
                  setSelectedProject(null);
                }}
                onCancel={() => {
                  setIsCreatingProject(false);
                  setSelectedProject(null);
                }}
                isNewProject={!selectedProject}
              />
            ) : (
              <>
                <ProjectsHeader>
                  <h3>All Projects</h3>
                  <CreateProjectButton 
                    onClick={handleCreateProject}
                  >
                    {renderIcon(FiPlus)} Create Project
                  </CreateProjectButton>
                </ProjectsHeader>
                
                <AllProjectsGrid>
                  {projects.length > 0 ? (
                    projects.map(project => (
                      <ProjectCard key={project.id} onClick={() => setSelectedProject(project)}>
                        <ProjectHeader>
                          <ProjectTitle>{project.name}</ProjectTitle>
                          <StatusBadge $status={project.status}>
                            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          </StatusBadge>
                        </ProjectHeader>
                        
                        <ProjectProgress $progress={project.progress} />
                        
                        <ProjectDates>
                          <span>Start: {new Date(project.start_date).toLocaleDateString()}</span>
                          <span>Due: {new Date(project.due_date).toLocaleDateString()}</span>
                        </ProjectDates>
                        
                        <ProjectCardActions>
                          <CardActionButton onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProject(project);
                          }}>
                            {renderIcon(FiFolder)} View Details
                          </CardActionButton>
                          
                          <CardActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              // Set up project for editing
                              setSelectedProject(project);
                              setIsCreatingProject(true);
                            }}
                            style={{ background: 'rgba(31, 83, 255, 0.2)' }}
                          >
                            {renderIcon(FiEdit)} Edit
                          </CardActionButton>
                          
                          <CardActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id);
                            }}
                            style={{ background: 'rgba(255, 59, 48, 0.2)' }}
                          >
                            {renderIcon(FiTrash2)} Delete
                          </CardActionButton>
                        </ProjectCardActions>
                      </ProjectCard>
                    ))
                  ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0' }}>
                      <p>No projects found for this client.</p>
                      <button 
                        style={{ 
                          marginTop: '20px',
                          padding: '10px 15px',
                          background: 'rgba(31, 83, 255, 0.25)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontWeight: '600'
                        }}
                        onClick={handleCreateProject}
                      >
                        {renderIcon(FiPlus)} Create First Project
                      </button>
                    </div>
                  )}
                </AllProjectsGrid>
              </>
            )}
          </ProjectsContent>
        );
        
      case 'files':
        return <ClientFileManager clientId={clientId} />;
        
      case 'tasks':
        return <TasksPage clientId={clientId} />;
        
      case 'checklist':
        // Show special checklist for Altare (client-004)
        if (clientId === 'client-004') {
          return <Client004Checklist />;
        }
        return <ClientChecklist clientId={clientId} />;
        
      case 'website-questionnaire':
        // Only show website questionnaire for Altare (client-004)
        if (clientId === 'client-004') {
          return <Client004WebsiteQuestionnaire />;
        }
        return null;
        
      case 'analytics':
        return (
          <AnalyticsContent>
            <h3>Client Analytics</h3>
            <React.Suspense fallback={<div>Loading analytics dashboard...</div>}>
              <AnalyticsDashboard clientId={clientId} dateRange="month" />
            </React.Suspense>
            <ComingSoonMessage>
              <h4>Coming Soon</h4>
              <p>We're working on advanced analytics features including:</p>
              <ul>
                <li>Project timeline visualization</li>
                <li>Budget tracking and financial reports</li>
                <li>Resource allocation insights</li>
                <li>Performance metrics and KPIs</li>
                <li>Custom reporting options</li>
              </ul>
              <div style={{ marginTop: '20px' }}>
                <button
                  onClick={() => window.open('mailto:sales@marketnest.com?subject=Interested in Analytics Features&body=Please contact me about enabling analytics.', '_blank')}
                  style={{
                    background: 'rgba(31, 83, 255, 0.25)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '10px 15px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FiMail size={16} /> Contact Sales
                </button>
              </div>
            </ComingSoonMessage>
          </AnalyticsContent>
        );
        
      case 'fundraiser':
        // Only show for Liberty Beans
        if (client?.id !== "client-liberty-beans") return null;
        return <FundraiserPage clientId={client.id} isAdmin={false} />;
        
      case 'seo':
        return <SEOAuditPage clientId={clientId} />;
        
      case 'keywords':
        return <KeywordsPage clientId={clientId} />;
      
      case 'competitor-analysis':
        return <CompetitorAnalysisPage clientId={clientId} />;
        
      case 'landing-pages':
        return (
          <div style={{ padding: '20px' }}>
            <h3>Landing Page Generator</h3>
            <p>Create and manage landing pages for {client?.name || 'your business'}.</p>
            <div style={{ marginTop: '20px' }}>
              <button 
                onClick={() => navigate(`/landing-page-generator?clientId=${clientId}`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: 'rgba(31, 83, 255, 0.25)',
                  color: 'white',
                  borderRadius: '5px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <FiPlus size={18} />
                Create Landing Page
              </button>
            </div>
          </div>
        );
        
      case 'ai-tools':
        return <ClientDashboardAI />;
        
      // Email case is handled above
        
      default:
        return null;
    }
  };

  const LIBERTY_BEANS_CLIENT_ID = "client-liberty-beans";
  const SOUTH_TEXAS_FAD_CLIENT_ID = "client-southtexasfad";
  const isMarketingLocked = client?.id === SOUTH_TEXAS_FAD_CLIENT_ID || client?.id === 'client-grayson-burnette';

  return (
    <ClientDashboardContainer>
      <ClientHeader>
        <BackToListButton onClick={onBack}>
          {renderIcon(FiArrowLeft)} Back to Clients
        </BackToListButton>
        
        <ClientHeaderInfo>
          <ClientName>
            {client?.name || 'Client'}
          </ClientName>
        </ClientHeaderInfo>
      </ClientHeader>

      <TabsContainer>
        {/* Always show these base tabs for all clients */}
        <TabButton $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</TabButton>
        <TabButton $active={activeTab === 'projects'} onClick={() => setActiveTab('projects')}>Projects</TabButton>
        <TabButton $active={activeTab === 'files'} onClick={() => setActiveTab('files')}>Files</TabButton>
        <TabButton $active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')}>Tasks</TabButton>
        <TabButton $active={activeTab === 'checklist'} onClick={() => setActiveTab('checklist')}>
          Checklist
        </TabButton>
        
        {/* Only show Website Questionnaire tab for client-004 (Altare) */}
        {client?.id === 'client-004' && (
          <TabButton $active={activeTab === 'website-questionnaire'} onClick={() => setActiveTab('website-questionnaire')}>
            Website Questionnaire
          </TabButton>
        )}
        
        {/* Show marketing tabs for all clients - they will still see the tabs but get an upsell message when clicked */}
        <TabButton 
          $active={activeTab === 'analytics'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('analytics')}
        >
          {isMarketingLocked && <FiLock />} Analytics
        </TabButton>
        <TabButton 
          $active={activeTab === 'email'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('email')}
        >
          {isMarketingLocked && <FiLock />} Email Marketing
        </TabButton>
        <TabButton 
          $active={activeTab === 'seo'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('seo')}
        >
          {isMarketingLocked && <FiLock />} SEO Audit
        </TabButton>
        <TabButton 
          $active={activeTab === 'keywords'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('keywords')}
        >
          {isMarketingLocked ? <FiLock /> : <FiKey style={{ marginRight: '5px' }} />} Keywords
        </TabButton>
        <TabButton 
          $active={activeTab === 'competitor-analysis'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('competitor-analysis')}
        >
          {isMarketingLocked ? <FiLock /> : <FiSearch style={{ marginRight: '5px' }} />} Competitor Analysis
        </TabButton>
        <TabButton 
          $active={activeTab === 'landing-pages'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('landing-pages')}
        >
          {isMarketingLocked && <FiLock />} Landing Pages
        </TabButton>
        <TabButton 
          $active={activeTab === 'ai-tools'} 
          $locked={isMarketingLocked}
          aria-disabled={isMarketingLocked}
          onClick={() => setActiveTab('ai-tools')}
        >
          {isMarketingLocked && <FiLock />} AI Tools
        </TabButton>
        
        {/* Only show Liberty Beans specific tabs */}
        {client?.id === LIBERTY_BEANS_CLIENT_ID && (
          <>
            <TabButton $active={activeTab === 'fundraiser'} onClick={() => setActiveTab('fundraiser')}>
              Fundraiser
            </TabButton>
          </>
        )}
      </TabsContainer>

      {renderContent()}
    </ClientDashboardContainer>
  );
};

export default ClientDashboard;